import PageHeader from "@/components/PageHeader";
import { ShieldCheck, Lock, Eye, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
  return (
    <div className="bg-white text-black min-h-screen">
      <PageHeader
        overline="Legal & Safety"
        title="Privacy"
        highlight="Policy"
        subtitle="Last Updated: August 9, 2026. Grooh is committed to protecting your privacy and managing your resident status securely."
        testid="privacy-page-header"
      />

      <section className="py-12 md:py-20 border-t border-black/10">
        <div className="container-x max-w-4xl mx-auto">
          {/* Main content grid */}
          <div className="space-y-12 md:space-y-16">

            {/* Intro */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg md:text-xl text-black/80 leading-relaxed font-medium">
                <strong>Grooh</strong> (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use the Grooh mobile application (the &quot;App&quot;) built for society management.
              </p>
              <p className="text-black/70 leading-relaxed mt-4">
                Please read this Privacy Policy carefully. By accessing or using the App, you agree to the collection and use of information in accordance with this policy.
              </p>
            </div>

            {/* 1. Information We Collect */}
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight mb-6 flex items-center gap-3 text-black">
                <span className="text-[#0033A2] text-2xl font-bold">01/</span> Information We Collect
              </h2>
              <p className="text-black/70 mb-6 leading-relaxed">
                To provide a seamless society management experience, we collect information that helps identify you and manage your resident status.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <h3 className="font-heading font-bold text-lg mb-4 text-[#0033A2]">A. Personal Data</h3>
                  <ul className="space-y-3 text-black/75 text-sm leading-relaxed list-disc pl-5">
                    <li><strong>Account Credentials:</strong> Name, email address, phone number, and password/OTP credentials.</li>
                    <li><strong>Society/Resident Profile Details:</strong> Flat number, block/tower name, member role (Owner, Tenant, Resident, Admin), and affiliated society name.</li>
                    <li><strong>Family Members:</strong> Names and contact details of family members associated with your household (added by you).</li>
                    <li><strong>Vehicles Information:</strong> Vehicle registration number, vehicle type (two-wheeler, four-wheeler), and model for parking and security tracking.</li>
                  </ul>
                </div>

                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                  <h3 className="font-heading font-bold text-lg mb-4 text-[#0033A2]">B. User-Generated Content</h3>
                  <ul className="space-y-3 text-black/75 text-sm leading-relaxed list-disc pl-5">
                    <li><strong>Maintenance & Billing Details:</strong> Transaction history, payment verification screenshots (e.g., bank receipts/UPI screenshots), and bill preferences.</li>
                    <li><strong>Documents:</strong> Resident NOCs, rules & regulations, meeting minutes, and other files uploaded by users or administrators.</li>
                    <li><strong>Help Desk / Complaints:</strong> Text descriptions, audio notes, and attachments associated with maintenance or service complaints.</li>
                    <li><strong>Polls & Votes:</strong> Your participation and responses in society voting/polling activities.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 2. Device Permissions */}
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight mb-6 flex items-center gap-3 text-black">
                <span className="text-[#0033A2] text-2xl font-bold">02/</span> Device Permissions
              </h2>
              <p className="text-black/70 mb-6 leading-relaxed">
                The App requests specific device permissions to enable core society management functionalities. Below is a detailed breakdown of these permissions:
              </p>

              <div className="overflow-x-auto border border-black/10 rounded-2xl">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-black/10">
                      <th className="p-4 font-heading font-bold text-black">Permission</th>
                      <th className="p-4 font-heading font-bold text-black">Technical Name</th>
                      <th className="p-4 font-heading font-bold text-black">Purpose in Grooh</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/10 text-black/80">
                    <tr>
                      <td className="p-4 font-semibold text-black">Internet Access</td>
                      <td className="p-4 font-mono text-xs">android.permission.INTERNET</td>
                      <td className="p-4">Required to communicate with our servers to fetch bills, update noticeboards, register payments, load directories, and sync user accounts in real time.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">Read Contacts</td>
                      <td className="p-4 font-mono text-xs">android.permission.READ_CONTACTS</td>
                      <td className="p-4">Allows you to import emergency contact numbers, family members, or neighbor details directly from your phone book into the society directory.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">Write Contacts</td>
                      <td className="p-4 font-mono text-xs">android.permission.WRITE_CONTACTS</td>
                      <td className="p-4">Allows you to save society emergency contacts, committee members, or facility managers directly to your device&apos;s address book from the directory.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">Read Storage</td>
                      <td className="p-4 font-mono text-xs">android.permission.READ_EXTERNAL_STORAGE</td>
                      <td className="p-4">Used to select and upload payment screenshots (UPI/bank receipts) for maintenance dues, profile photos, and documents for complaint submission.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">Write Storage</td>
                      <td className="p-4 font-mono text-xs">android.permission.WRITE_EXTERNAL_STORAGE</td>
                      <td className="p-4">Allows you to download and save maintenance invoices, payment receipts, society notices, rules sheets, and shared documents onto your device storage.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">Record Audio</td>
                      <td className="p-4 font-mono text-xs">android.permission.RECORD_AUDIO</td>
                      <td className="p-4">Enables the capture of voice descriptions or audio clips when filing a complaint or sending messages to support/security.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">Vibration</td>
                      <td className="p-4 font-mono text-xs">android.permission.VIBRATE</td>
                      <td className="p-4">Provides physical/haptic feedback for user interactions (e.g., successful votes, payment submissions) and urgent alert notifications.</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-black">System Alert Overlay</td>
                      <td className="p-4 font-mono text-xs">android.permission.SYSTEM_ALERT_WINDOW</td>
                      <td className="p-4">Used primarily during troubleshooting, development testing, and displaying critical real-time alert dialogs over other applications.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3. How We Use */}
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight mb-6 flex items-center gap-3 text-black">
                <span className="text-[#0033A2] text-2xl font-bold">03/</span> How We Use Your Information
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { title: "Account Management", desc: "To create, maintain, and verify your account and society membership." },
                  { title: "Communication & Notices", desc: "To distribute society announcements, official notices, and meeting reminders." },
                  { title: "Financial Ledger & Receipts", desc: "To track maintenance dues, generate invoices, verify user-submitted payments, and issue receipts." },
                  { title: "Operational Workflows", desc: "To process booking requests for amenities (e.g., Clubhouse, Gym) and manage community voting." },
                  { title: "Service Requests", desc: "To route maintenance complaints to society technicians and track their resolution." },
                  { title: "Safety & Security", desc: "To record registered vehicles and emergency contacts for a secure living environment." }
                ].map((item, idx) => (
                  <div key={idx} className="p-5 border border-black/10 rounded-2xl hover:border-[#0033A2] transition-colors">
                    <h3 className="font-heading font-bold text-black mb-1.5">{item.title}</h3>
                    <p className="text-sm text-black/70 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 4. Sharing */}
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight mb-6 flex items-center gap-3 text-black">
                <span className="text-[#0033A2] text-2xl font-bold">04/</span> Data Sharing & Disclosure
              </h2>
              <p className="text-black/70 mb-4 leading-relaxed">
                We respect your privacy and do not sell, rent, or trade your personal information. We may share data only under the following circumstances:
              </p>
              <ul className="space-y-3 text-black/75 text-sm pl-5 list-disc">
                <li><strong>With Society Administrators:</strong> Designated society committee members and admins have access to billing status, directory profiles, and complaint logs to govern the society.</li>
                <li><strong>Legal Compliance:</strong> We may disclose information if required to do so by law, court order, or governmental authority.</li>
                <li><strong>Service Providers:</strong> We may share data with trusted third-party service providers (such as hosting, database, and payment gateways) who assist us in operating the App, subject to confidentiality agreements.</li>
              </ul>
            </div>

            {/* 5. Security */}
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight mb-6 flex items-center gap-3 text-black">
                <span className="text-[#0033A2] text-2xl font-bold">05/</span> Data Security
              </h2>
              <div className="flex gap-4 p-6 bg-slate-50 border border-slate-100 rounded-2xl items-start">
                <Lock className="text-[#0033A2] shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-heading font-bold text-black mb-2">Industry-Standard Security</h3>
                  <p className="text-sm text-black/70 leading-relaxed">
                    We implement industry-standard security measures (such as SSL encryption and access controls) to safeguard your data against unauthorized access, loss, or alteration. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                  </p>
                </div>
              </div>
            </div>

            {/* 6. Retention and Deletion */}
            <div>
              <h2 className="font-heading font-black text-3xl tracking-tight mb-6 flex items-center gap-3 text-black">
                <span className="text-[#0033A2] text-2xl font-bold">06/</span> Data Retention & Account Deletion
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-lg text-black mb-2">A. Retention Policy</h3>
                  <p className="text-sm text-black/70 leading-relaxed">
                    We retain your personal data for as long as you remain a registered resident/member of the society. We also retain billing and transaction records as required to comply with financial and legal obligations.
                  </p>
                </div>

                <div>
                  <h3 className="font-heading font-bold text-lg text-black mb-2">B. Data & Account Deletion (Google Play Compliance)</h3>
                  <p className="text-sm text-black/70 leading-relaxed mb-4">
                    You have the right to request the deletion of your account and all associated personal data.
                  </p>

                  <div className="grid gap-4">
                    <div className="p-5 border border-red-200 bg-red-50/50 rounded-2xl">
                      <h4 className="font-bold text-red-600 mb-2 text-sm uppercase tracking-wider">Account Deletion Request</h4>
                      <p className="text-xs text-black/75 leading-relaxed">
                        To delete your account and associated data, you can request account deletion using our online request form or by emailing support.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="text-sm text-black/75 leading-relaxed">
                      <strong className="text-black">Data Removed:</strong> Upon successful processing of a deletion request, we permanently delete your profile details, family member contacts, vehicle registrations, and active service requests.
                    </div>
                    <div className="text-sm text-black/75 leading-relaxed">
                      <strong className="text-black">Data Retained for Legal/Regulatory Compliance:</strong> Historical maintenance payment transactions, billing statements, and official society records will be archived or anonymized to maintain the financial audits of the housing society.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 7. Contact Us */}
            <div className="p-8 bg-slate-900 text-white rounded-3xl flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h3 className="font-heading font-black text-2xl mb-2">Questions or concerns?</h3>
                <p className="text-white/70 text-sm max-w-md">
                  If you have any questions or concerns about this Privacy Policy or our data practices, please contact us.
                </p>
              </div>
              <div className="space-y-2 text-sm shrink-0">
                <p>Email: <a href="mailto:hello@grooh.app" className="underline hover:text-[#0033A2] transition-colors">hello@grooh.app</a></p>
                {/* <p>Website: <a href="https://bytespark.co" target="_blank" rel="noreferrer" className="underline hover:text-[#0033A2] transition-colors">https://bytespark.co</a></p> */}
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
